import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { 
  Plus, Trash2, Edit2, Save, X, Gamepad2, Dice5, Smartphone,
  ChevronRight, ChevronDown, GripVertical, Search,
  Sparkles, Image, ShoppingBag, Star, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ImageUploader from "./ImageUploader";
import PdfUploader from "./PdfUploader";
import adminApi from "@/lib/adminApi";

const gameTypes = [
  { value: "videogame", label: "Videojuego", icon: Gamepad2 },
  { value: "boardgame", label: "Juego de Mesa", icon: Dice5 },
  { value: "mobile", label: "Móvil", icon: Smartphone },
];

const gameStatuses = [
  { value: "coming_soon", label: "Próximamente" },
  { value: "new", label: "¡Nuevo!" },
  { value: "available", label: "Disponible" },
  { value: "early_access", label: "Early Access" },
];

const subsections = [
  { id: "features", label: "Features", icon: Sparkles },
  { id: "gallery", label: "Galería", icon: Image },
  { id: "stores", label: "Tiendas", icon: ShoppingBag },
  { id: "testimonials", label: "Reseñas", icon: Star },
];

export default function GamesManagerTab({ games, onRefresh }) {
  // Expansion state
  const [expandedGames, setExpandedGames] = useState(new Set());
  const [activeSubsection, setActiveSubsection] = useState({}); // { gameId: 'features' | 'gallery' | ... }
  
  // Edit mode state
  const [editingGame, setEditingGame] = useState(null);
  const [creatingGame, setCreatingGame] = useState(false);
  
  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Game form
  const [gameForm, setGameForm] = useState({
    title: "", slug: "", tagline: "", description: "",
    logo_url: "", thumbnail_url: "", background_url: "",
    promo_image_url: "", hero_image_url: "", trailer_url: "",
    instructions_pdf_url: "", game_type: "videogame", status: "available",
    release_date: "", genre: "", players: "", languages: "",
    requirements: "", order: 0, active: true
  });

  // Subsection data
  const [features, setFeatures] = useState({});
  const [gallery, setGallery] = useState({});
  const [stores, setStores] = useState({});
  const [testimonials, setTestimonials] = useState({});

  // Subsection forms
  const [featureForm, setFeatureForm] = useState({ icon: "", title: "", description: "", order: 0 });
  const [galleryForm, setGalleryForm] = useState({ image_url: "", caption: "", order: 0 });
  const [storeForm, setStoreForm] = useState({ name: "", url: "", icon: "", order: 0 });
  const [testimonialForm, setTestimonialForm] = useState({ author: "", role: "", content: "", rating: 5, order: 0 });
  
  // Editing subsection items
  const [editingFeature, setEditingFeature] = useState(null);
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [editingStore, setEditingStore] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);

  const sortedGames = useMemo(() => 
    [...games].sort((a, b) => (a.order || 0) - (b.order || 0)), 
    [games]
  );

  const filteredGames = useMemo(() => {
    if (!searchQuery.trim()) return sortedGames;
    const query = searchQuery.toLowerCase();
    return sortedGames.filter(game => 
      game.title.toLowerCase().includes(query) ||
      game.tagline?.toLowerCase().includes(query)
    );
  }, [sortedGames, searchQuery]);

  // Fetch subsection data when game is expanded
  const fetchSubsectionData = async (gameId, subsection) => {
    try {
      const res = await adminApi.get(`/games/${gameId}/${subsection}`);
      if (subsection === 'features') setFeatures(prev => ({ ...prev, [gameId]: res.data }));
      else if (subsection === 'gallery') setGallery(prev => ({ ...prev, [gameId]: res.data }));
      else if (subsection === 'stores') setStores(prev => ({ ...prev, [gameId]: res.data }));
      else if (subsection === 'testimonials') setTestimonials(prev => ({ ...prev, [gameId]: res.data }));
    } catch (error) {
      console.error(`Error fetching ${subsection}:`, error);
    }
  };

  // Toggle game expansion
  const toggleGame = async (gameId) => {
    setExpandedGames(prev => {
      const next = new Set(prev);
      if (next.has(gameId)) {
        next.delete(gameId);
      } else {
        next.add(gameId);
        // Set default subsection and fetch data
        if (!activeSubsection[gameId]) {
          setActiveSubsection(prev => ({ ...prev, [gameId]: 'features' }));
          fetchSubsectionData(gameId, 'features');
        }
      }
      return next;
    });
  };

  // Change active subsection for a game
  const changeSubsection = (gameId, subsection) => {
    setActiveSubsection(prev => ({ ...prev, [gameId]: subsection }));
    // Fetch data if not already loaded
    const dataMap = { features, gallery, stores, testimonials };
    if (!dataMap[subsection][gameId]) {
      fetchSubsectionData(gameId, subsection);
    }
  };

  // Generate slug
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  // Reset forms
  const resetGameForm = () => {
    setEditingGame(null);
    setCreatingGame(false);
    setGameForm({
      title: "", slug: "", tagline: "", description: "",
      logo_url: "", thumbnail_url: "", background_url: "",
      promo_image_url: "", hero_image_url: "", trailer_url: "",
      instructions_pdf_url: "", game_type: "videogame", status: "available",
      release_date: "", genre: "", players: "", languages: "",
      requirements: "", order: 0, active: true
    });
  };

  const resetSubsectionForms = () => {
    setEditingFeature(null);
    setEditingGalleryItem(null);
    setEditingStore(null);
    setEditingTestimonial(null);
    setFeatureForm({ icon: "", title: "", description: "", order: 0 });
    setGalleryForm({ image_url: "", caption: "", order: 0 });
    setStoreForm({ name: "", url: "", icon: "", order: 0 });
    setTestimonialForm({ author: "", role: "", content: "", rating: 5, order: 0 });
  };

  // Start creating/editing game
  const startCreateGame = () => {
    resetGameForm();
    resetSubsectionForms();
    setCreatingGame(true);
    setGameForm(prev => ({ ...prev, order: sortedGames.length }));
  };

  const startEditGame = (game) => {
    resetSubsectionForms();
    setEditingGame(game);
    setCreatingGame(false);
    setGameForm({
      title: game.title || "",
      slug: game.slug || "",
      tagline: game.tagline || "",
      description: game.description || "",
      logo_url: game.logo_url || "",
      thumbnail_url: game.thumbnail_url || "",
      background_url: game.background_url || "",
      promo_image_url: game.promo_image_url || "",
      hero_image_url: game.hero_image_url || "",
      trailer_url: game.trailer_url || "",
      instructions_pdf_url: game.instructions_pdf_url || "",
      game_type: game.game_type || "videogame",
      status: game.status || "available",
      release_date: game.release_date || "",
      genre: game.genre || "",
      players: game.players || "",
      languages: game.languages || "",
      requirements: game.requirements || "",
      order: game.order || 0,
      active: game.active !== false
    });
  };

  // Submit game
  const handleGameSubmit = async (e) => {
    e.preventDefault();
    if (!gameForm.title.trim()) {
      toast.error("El título es requerido");
      return;
    }
    const data = { ...gameForm, slug: gameForm.slug || generateSlug(gameForm.title) };
    try {
      if (editingGame) {
        await adminApi.put(`/games/${editingGame.id}`, data);
        toast.success("Juego actualizado");
      } else {
        await adminApi.post(`/games`, data);
        toast.success("Juego creado");
      }
      resetGameForm();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar juego");
    }
  };

  // Delete game
  const handleDeleteGame = async (gameId) => {
    if (!confirm("¿Eliminar este juego y todos sus elementos?")) return;
    try {
      await adminApi.delete(`/games/${gameId}`);
      toast.success("Juego eliminado");
      onRefresh();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Feature handlers
  const handleFeatureSubmit = async (e, gameId) => {
    e.preventDefault();
    if (!featureForm.title.trim()) {
      toast.error("El título es requerido");
      return;
    }
    try {
      if (editingFeature) {
        await adminApi.put(`/games/features/${editingFeature.id}`, featureForm);
        toast.success("Feature actualizado");
      } else {
        await adminApi.post(`/games/features`, { ...featureForm, game_id: gameId });
        toast.success("Feature creado");
      }
      resetSubsectionForms();
      fetchSubsectionData(gameId, 'features');
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleDeleteFeature = async (featureId, gameId) => {
    try {
      await adminApi.delete(`/games/features/${featureId}`);
      toast.success("Feature eliminado");
      fetchSubsectionData(gameId, 'features');
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Gallery handlers
  const handleGallerySubmit = async (e, gameId) => {
    e.preventDefault();
    if (!galleryForm.image_url.trim()) {
      toast.error("La imagen es requerida");
      return;
    }
    try {
      if (editingGalleryItem) {
        await adminApi.put(`/games/gallery/${editingGalleryItem.id}`, galleryForm);
        toast.success("Imagen actualizada");
      } else {
        await adminApi.post(`/games/gallery`, { ...galleryForm, game_id: gameId });
        toast.success("Imagen agregada");
      }
      resetSubsectionForms();
      fetchSubsectionData(gameId, 'gallery');
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleDeleteGalleryItem = async (itemId, gameId) => {
    try {
      await adminApi.delete(`/games/gallery/${itemId}`);
      toast.success("Imagen eliminada");
      fetchSubsectionData(gameId, 'gallery');
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Store handlers
  const handleStoreSubmit = async (e, gameId) => {
    e.preventDefault();
    if (!storeForm.name.trim() || !storeForm.url.trim()) {
      toast.error("Nombre y URL son requeridos");
      return;
    }
    try {
      if (editingStore) {
        await adminApi.put(`/games/stores/${editingStore.id}`, storeForm);
        toast.success("Tienda actualizada");
      } else {
        await adminApi.post(`/games/stores`, { ...storeForm, game_id: gameId });
        toast.success("Tienda agregada");
      }
      resetSubsectionForms();
      fetchSubsectionData(gameId, 'stores');
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleDeleteStore = async (storeId, gameId) => {
    try {
      await adminApi.delete(`/games/stores/${storeId}`);
      toast.success("Tienda eliminada");
      fetchSubsectionData(gameId, 'stores');
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Testimonial handlers
  const handleTestimonialSubmit = async (e, gameId) => {
    e.preventDefault();
    if (!testimonialForm.author.trim() || !testimonialForm.content.trim()) {
      toast.error("Autor y contenido son requeridos");
      return;
    }
    try {
      if (editingTestimonial) {
        await adminApi.put(`/games/testimonials/${editingTestimonial.id}`, testimonialForm);
        toast.success("Reseña actualizada");
      } else {
        await adminApi.post(`/games/testimonials`, { ...testimonialForm, game_id: gameId });
        toast.success("Reseña agregada");
      }
      resetSubsectionForms();
      fetchSubsectionData(gameId, 'testimonials');
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleDeleteTestimonial = async (testimonialId, gameId) => {
    try {
      await adminApi.delete(`/games/testimonials/${testimonialId}`);
      toast.success("Reseña eliminada");
      fetchSubsectionData(gameId, 'testimonials');
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Get icon for game type
  const getGameTypeIcon = (type) => {
    const found = gameTypes.find(t => t.value === type);
    return found ? found.icon : Gamepad2;
  };

  const hasActiveForm = editingGame || creatingGame;

  return (
    <div className="w-full max-w-full lg:grid lg:grid-cols-5 lg:gap-6">
      {/* Main Content - Tree View */}
      <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-4 md:p-5 shadow-sm w-full" data-testid="games-tree">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h2 className="text-base md:text-xl font-bold text-black flex items-center gap-2">
            <Gamepad2 size={18} className="md:w-[22px] md:h-[22px] flex-shrink-0" />
            Juegos
          </h2>
          <Button 
            onClick={startCreateGame} 
            className="bg-black text-white rounded-full text-xs md:text-base px-3 py-1.5 md:px-4 md:py-2 flex-shrink-0"
            data-testid="add-game-btn"
          >
            <Plus size={14} className="mr-1 md:w-4 md:h-4" />
            Juego
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar juegos..."
            className="pl-9 bg-gray-50 border-gray-200 text-base"
            data-testid="games-search"
          />
        </div>

        {/* Tree */}
        <div className="space-y-1 overflow-y-auto overflow-x-hidden max-h-[50vh] md:max-h-[60vh] lg:max-h-[600px]">
          {filteredGames.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              {games.length === 0 ? "No hay juegos. Crea uno para comenzar." : "No se encontraron resultados."}
            </p>
          ) : (
            filteredGames.map((game) => {
              const isExpanded = expandedGames.has(game.id);
              const isActive = editingGame?.id === game.id;
              const GameTypeIcon = getGameTypeIcon(game.game_type);
              const currentSubsection = activeSubsection[game.id] || 'features';

              return (
                <div key={game.id} className="select-none">
                  {/* Game Row */}
                  <div 
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                      isActive ? 'bg-pink-100 border border-[#E91E8C]' : 'hover:bg-gray-100'
                    }`}
                    data-testid={`game-row-${game.id}`}
                  >
                    <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                      <GripVertical size={16} />
                    </div>
                    <button 
                      onClick={() => toggleGame(game.id)}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {game.thumbnail_url ? (
                      <img src={game.thumbnail_url} alt="" className="w-10 h-10 object-cover rounded" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                        <GameTypeIcon size={20} className="text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0" onClick={() => toggleGame(game.id)}>
                      <p className="font-semibold text-black truncate">{game.title}</p>
                      <p className="text-xs text-gray-500 truncate">{game.tagline || game.genre || 'Sin descripción'}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEditGame(game)} className="h-7 w-7 p-0">
                        <Edit2 size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteGame(game.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="ml-6 border-l-2 border-gray-200 pl-3 mt-2 mb-4">
                      {/* Subsection Tabs */}
                      <div className="flex gap-1 mb-3 flex-wrap">
                        {subsections.map((sub) => (
                          <button
                            key={sub.id}
                            onClick={() => changeSubsection(game.id, sub.id)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                              currentSubsection === sub.id
                                ? 'bg-black text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <sub.icon size={12} />
                            {sub.label}
                          </button>
                        ))}
                      </div>

                      {/* Subsection Content */}
                      {currentSubsection === 'features' && (
                        <SubsectionFeatures
                          gameId={game.id}
                          items={features[game.id] || []}
                          form={featureForm}
                          setForm={setFeatureForm}
                          editing={editingFeature}
                          setEditing={setEditingFeature}
                          onSubmit={handleFeatureSubmit}
                          onDelete={handleDeleteFeature}
                          onReset={resetSubsectionForms}
                        />
                      )}

                      {currentSubsection === 'gallery' && (
                        <SubsectionGallery
                          gameId={game.id}
                          items={gallery[game.id] || []}
                          form={galleryForm}
                          setForm={setGalleryForm}
                          editing={editingGalleryItem}
                          setEditing={setEditingGalleryItem}
                          onSubmit={handleGallerySubmit}
                          onDelete={handleDeleteGalleryItem}
                          onReset={resetSubsectionForms}
                        />
                      )}

                      {currentSubsection === 'stores' && (
                        <SubsectionStores
                          gameId={game.id}
                          items={stores[game.id] || []}
                          form={storeForm}
                          setForm={setStoreForm}
                          editing={editingStore}
                          setEditing={setEditingStore}
                          onSubmit={handleStoreSubmit}
                          onDelete={handleDeleteStore}
                          onReset={resetSubsectionForms}
                        />
                      )}

                      {currentSubsection === 'testimonials' && (
                        <SubsectionTestimonials
                          gameId={game.id}
                          items={testimonials[game.id] || []}
                          form={testimonialForm}
                          setForm={setTestimonialForm}
                          editing={editingTestimonial}
                          setEditing={setEditingTestimonial}
                          onSubmit={handleTestimonialSubmit}
                          onDelete={handleDeleteTestimonial}
                          onReset={resetSubsectionForms}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Desktop: Form Panel */}
      <div className="hidden lg:block lg:col-span-2">
        {!hasActiveForm ? (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center sticky top-4">
            <Gamepad2 size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Selecciona un juego para editar o crea uno nuevo</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm sticky top-4">
            <GameFormContent
              form={gameForm}
              setForm={setGameForm}
              editing={editingGame}
              creating={creatingGame}
              onSubmit={handleGameSubmit}
              onReset={resetGameForm}
            />
          </div>
        )}
      </div>

      {/* Mobile: Drawer */}
      {hasActiveForm && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={resetGameForm}
          />
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto lg:hidden">
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>
            <GameFormContent
              form={gameForm}
              setForm={setGameForm}
              editing={editingGame}
              creating={creatingGame}
              onSubmit={handleGameSubmit}
              onReset={resetGameForm}
            />
          </div>
        </>
      )}
    </div>
  );
}

// Game Form Component
function GameFormContent({ form, setForm, editing, creating, onSubmit, onReset }) {
  return (
    <div className="p-4 md:p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-black flex items-center gap-2">
          <Gamepad2 size={20} />
          {creating ? "Nuevo Juego" : "Editar Juego"}
        </h3>
        <Button variant="ghost" size="sm" onClick={onReset} className="h-8 w-8 p-0">
          <X size={16} />
        </Button>
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1 font-medium">Título *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            required
            placeholder="Nombre del juego"
            className="bg-gray-50 border-gray-200"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1 font-medium">Tagline</label>
          <Input
            value={form.tagline}
            onChange={(e) => setForm({...form, tagline: e.target.value})}
            placeholder="Frase corta descriptiva"
            className="bg-gray-50 border-gray-200"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">Tipo</label>
            <Select value={form.game_type} onValueChange={(v) => setForm({...form, game_type: v})}>
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {gameTypes.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">Estado</label>
            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {gameStatuses.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1 font-medium">Descripción</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
            placeholder="Descripción del juego"
            className="w-full min-h-[80px] px-3 py-2 rounded-md border border-gray-200 bg-gray-50 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1 font-medium">Thumbnail</label>
          <ImageUploader
            value={form.thumbnail_url}
            onChange={(url) => setForm({...form, thumbnail_url: url})}
            category="thumbnails"
            recommendedSize="400x400 px"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1 font-medium">URL del Trailer</label>
          <Input
            value={form.trailer_url}
            onChange={(e) => setForm({...form, trailer_url: e.target.value})}
            placeholder="https://youtube.com/..."
            className="bg-gray-50 border-gray-200"
          />
        </div>
        <div className="flex gap-2 pt-2 pb-4 lg:pb-0">
          <Button type="submit" className="flex-1 bg-black text-white rounded-full h-12 lg:h-10 text-base">
            <Save size={16} className="mr-2" />
            {creating ? "Crear" : "Guardar"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Features Subsection
function SubsectionFeatures({ gameId, items, form, setForm, editing, setEditing, onSubmit, onDelete, onReset }) {
  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  return (
    <div className="space-y-2">
      {sortedItems.map(item => (
        <div key={item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm">
          <span className="text-lg">{item.icon || '✨'}</span>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{item.title}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(item); setForm({ icon: item.icon || '', title: item.title, description: item.description || '', order: item.order || 0 }); }} className="h-6 w-6 p-0">
            <Edit2 size={12} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(item.id, gameId)} className="h-6 w-6 p-0 text-red-500">
            <Trash2 size={12} />
          </Button>
        </div>
      ))}
      
      {/* Add/Edit Form */}
      <form onSubmit={(e) => onSubmit(e, gameId)} className="flex gap-2 items-end mt-2">
        <Input
          value={form.icon}
          onChange={(e) => setForm({...form, icon: e.target.value})}
          placeholder="🎮"
          className="w-12 text-center bg-white border-gray-200 text-sm"
        />
        <Input
          value={form.title}
          onChange={(e) => setForm({...form, title: e.target.value})}
          placeholder="Título del feature"
          className="flex-1 bg-white border-gray-200 text-sm"
        />
        <Button type="submit" size="sm" className="bg-black text-white h-9 px-3">
          {editing ? <Save size={14} /> : <Plus size={14} />}
        </Button>
        {editing && (
          <Button type="button" size="sm" variant="ghost" onClick={onReset} className="h-9 px-2">
            <X size={14} />
          </Button>
        )}
      </form>
    </div>
  );
}

// Gallery Subsection
function SubsectionGallery({ gameId, items, form, setForm, editing, setEditing, onSubmit, onDelete, onReset }) {
  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {sortedItems.map(item => (
          <div key={item.id} className="relative group">
            <img src={item.image_url} alt={item.caption || ''} className="w-full h-16 object-cover rounded" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded">
              <Button size="sm" variant="ghost" onClick={() => { setEditing(item); setForm({ image_url: item.image_url, caption: item.caption || '', order: item.order || 0 }); }} className="h-6 w-6 p-0 text-white">
                <Edit2 size={12} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDelete(item.id, gameId)} className="h-6 w-6 p-0 text-red-400">
                <Trash2 size={12} />
              </Button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Add Form */}
      <div className="mt-2">
        <ImageUploader
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          category="thumbnails"
          recommendedSize="800x600 px"
        />
        {form.image_url && (
          <div className="flex gap-2 mt-2">
            <Input
              value={form.caption}
              onChange={(e) => setForm({...form, caption: e.target.value})}
              placeholder="Descripción (opcional)"
              className="flex-1 bg-white border-gray-200 text-sm"
            />
            <Button onClick={(e) => onSubmit(e, gameId)} size="sm" className="bg-black text-white h-9 px-3">
              {editing ? <Save size={14} /> : <Plus size={14} />}
            </Button>
            {editing && (
              <Button type="button" size="sm" variant="ghost" onClick={onReset} className="h-9 px-2">
                <X size={14} />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Stores Subsection
function SubsectionStores({ gameId, items, form, setForm, editing, setEditing, onSubmit, onDelete, onReset }) {
  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  return (
    <div className="space-y-2">
      {sortedItems.map(item => (
        <div key={item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm">
          {item.icon && <span className="text-lg">{item.icon}</span>}
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{item.name}</p>
            <p className="text-xs text-gray-500 truncate">{item.url}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(item); setForm({ name: item.name, url: item.url, icon: item.icon || '', order: item.order || 0 }); }} className="h-6 w-6 p-0">
            <Edit2 size={12} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(item.id, gameId)} className="h-6 w-6 p-0 text-red-500">
            <Trash2 size={12} />
          </Button>
        </div>
      ))}
      
      {/* Add/Edit Form */}
      <form onSubmit={(e) => onSubmit(e, gameId)} className="space-y-2 mt-2">
        <div className="flex gap-2">
          <Input
            value={form.icon}
            onChange={(e) => setForm({...form, icon: e.target.value})}
            placeholder="🛒"
            className="w-12 text-center bg-white border-gray-200 text-sm"
          />
          <Input
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            placeholder="Nombre de tienda"
            className="flex-1 bg-white border-gray-200 text-sm"
          />
        </div>
        <div className="flex gap-2">
          <Input
            value={form.url}
            onChange={(e) => setForm({...form, url: e.target.value})}
            placeholder="https://..."
            className="flex-1 bg-white border-gray-200 text-sm"
          />
          <Button type="submit" size="sm" className="bg-black text-white h-9 px-3">
            {editing ? <Save size={14} /> : <Plus size={14} />}
          </Button>
          {editing && (
            <Button type="button" size="sm" variant="ghost" onClick={onReset} className="h-9 px-2">
              <X size={14} />
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

// Testimonials Subsection
function SubsectionTestimonials({ gameId, items, form, setForm, editing, setEditing, onSubmit, onDelete, onReset }) {
  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  return (
    <div className="space-y-2">
      {sortedItems.map(item => (
        <div key={item.id} className="p-2 bg-gray-50 rounded-lg text-sm">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">{item.author} {item.role && `• ${item.role}`}</p>
              <p className="text-sm truncate">{item.content}</p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => { setEditing(item); setForm({ author: item.author, role: item.role || '', content: item.content, rating: item.rating || 5, order: item.order || 0 }); }} className="h-6 w-6 p-0">
                <Edit2 size={12} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDelete(item.id, gameId)} className="h-6 w-6 p-0 text-red-500">
                <Trash2 size={12} />
              </Button>
            </div>
          </div>
        </div>
      ))}
      
      {/* Add/Edit Form */}
      <form onSubmit={(e) => onSubmit(e, gameId)} className="space-y-2 mt-2">
        <div className="flex gap-2">
          <Input
            value={form.author}
            onChange={(e) => setForm({...form, author: e.target.value})}
            placeholder="Autor"
            className="flex-1 bg-white border-gray-200 text-sm"
          />
          <Input
            value={form.role}
            onChange={(e) => setForm({...form, role: e.target.value})}
            placeholder="Rol (opcional)"
            className="flex-1 bg-white border-gray-200 text-sm"
          />
        </div>
        <div className="flex gap-2">
          <Input
            value={form.content}
            onChange={(e) => setForm({...form, content: e.target.value})}
            placeholder="Contenido de la reseña"
            className="flex-1 bg-white border-gray-200 text-sm"
          />
          <Button type="submit" size="sm" className="bg-black text-white h-9 px-3">
            {editing ? <Save size={14} /> : <Plus size={14} />}
          </Button>
          {editing && (
            <Button type="button" size="sm" variant="ghost" onClick={onReset} className="h-9 px-2">
              <X size={14} />
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
